
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Congregation, SermonPlan } from "@/hooks/sermon-plans/types";
import { useForm } from "react-hook-form";
import { EssentialDetailsTab } from "./EssentialDetailsTab";
import { AdditionalInfoTab } from "./AdditionalInfoTab";
import { SermonNotesField } from "./SermonNotesField";
import { SermonFormData } from "@/hooks/sermon-plans/types";
import { Form } from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CongregationInsightButton } from "./CongregationInsightButton";
import { FormActionButtons } from "./FormActionButtons";
import { useSermonFormState } from "./useSermonFormState";

interface SermonPlanFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (formData: SermonFormData) => void;
  currentSermonPlan: SermonPlan | null;
  initialFormData?: SermonFormData | null;
  congregations: Congregation[];
}

export function SermonPlanForm({
  open,
  onOpenChange,
  onSubmit,
  currentSermonPlan,
  initialFormData = null,
  congregations,
}: SermonPlanFormProps) {
  const form = useForm<SermonFormData>({
    defaultValues: {
      title: "",
      scripture_reference: "",
      notes: "",
      planned_date: "",
      theme: "",
      target_duration: "30",
      key_points: "",
      sermon_type: "Expository",
      congregation_id: ""
    },
    mode: "onChange"
  });

  const { date, setDate } = useSermonFormState({ 
    form, 
    currentSermonPlan, 
    initialFormData, 
    open 
  });

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    form.handleSubmit((data: SermonFormData) => {
      onSubmit(data);
    })(e);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[650px] max-h-[90vh] flex flex-col p-0 overflow-hidden" 
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-xl">
            {currentSermonPlan ? "Edit Sermon Plan" : "Create New Sermon Plan"}
          </DialogTitle>
        </DialogHeader>
        
        <CongregationInsightButton form={form} />
        
        <Form {...form}>
          <form onSubmit={handleFormSubmit} className="flex flex-col flex-1 overflow-hidden">
            <ScrollArea className="flex-1 px-6 overflow-auto max-h-[60vh]">
              <div className="py-4 space-y-6">
                <EssentialDetailsTab 
                  form={form} 
                  date={date} 
                  setDate={setDate} 
                />
                
                <AdditionalInfoTab form={form} />
              
                <SermonNotesField form={form} />
              </div>
            </ScrollArea>

            <FormActionButtons 
              currentSermonPlan={currentSermonPlan} 
              onCancel={() => onOpenChange(false)} 
            />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
